import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Plane,
  Send,
  FileText,
  Users,
  Briefcase,
  MessageSquare,
  Download,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    inquiry_type: "general",
    message: "",
  });

  useEffect(() => {
    const type = searchParams.get("type");
    if (type) {
      setFormData((prev) => ({ ...prev, inquiry_type: type }));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Inquiry submitted successfully! We will contact you shortly.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        inquiry_type: "general",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const inquiryTypes = [
    { value: "capability_statement", label: "Request Capability Statement", icon: FileText },
    { value: "partnership", label: "Partner With Us", icon: Users },
    { value: "contracting", label: "Contracting Inquiry", icon: Briefcase },
    { value: "general", label: "General Inquiry", icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#1a365d]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CONTACT</p>
            <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">
              GET IN TOUCH
            </h1>
            <p className="text-slate-200 text-lg leading-relaxed">
              Contact AMR Global Holdings LLC for government contracting inquiries, 
              partnership opportunities, or to request our capability statement. 
              Our team is ready to support your mission.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">INFO</p>
                <h2 className="font-heading text-2xl text-[#1a365d] mb-6">CONTACT INFORMATION</h2>
              </div>

              <Card className="card-tactical" data-testid="contact-card-phone">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1a365d] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#c5a04a]" />
                  </div>
                  <div>
                    <p className="font-heading text-[#1a365d] mb-1">PHONE</p>
                    <a href="tel:504-319-7683" className="text-slate-600 hover:text-[#c5a04a] transition-colors">
                      504-319-7683
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-tactical" data-testid="contact-card-email">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1a365d] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#c5a04a]" />
                  </div>
                  <div>
                    <p className="font-heading text-[#1a365d] mb-1">EMAIL</p>
                    <a href="mailto:AMREID@AMRGLOBALHOLDINGS.COM" className="text-slate-600 hover:text-[#c5a04a] transition-colors text-sm break-all">
                      AMREID@AMRGLOBALHOLDINGS.COM
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-tactical" data-testid="contact-card-address">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1a365d] rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#c5a04a]" />
                  </div>
                  <div>
                    <p className="font-heading text-[#1a365d] mb-1">ADDRESS</p>
                    <p className="text-slate-600 text-sm">
                      6001 Stars and Stripes Blvd<br />
                      Suite 244<br />
                      New Orleans, LA 70126
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-tactical" data-testid="contact-card-airport">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#c5a04a] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Plane className="w-6 h-6 text-[#1a365d]" />
                  </div>
                  <div>
                    <p className="font-heading text-[#1a365d] mb-1">HEADQUARTERS</p>
                    <p className="text-slate-600 text-sm">
                      New Orleans Lakefront Airport<br />
                      Airport Code: KNEW
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Capability Statement Preview */}
              <div className="mt-8">
                <Card className="capability-card" data-testid="capability-preview">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <Download className="w-8 h-8 text-[#c5a04a]" />
                      <div>
                        <p className="font-heading text-[#1a365d]">CAPABILITY STATEMENT</p>
                        <p className="text-slate-600 text-xs">Available upon request</p>
                      </div>
                    </div>
                    <img
                      src="https://customer-assets.emergentagent.com/job_rapid-deploy-52/artifacts/ftkxkyhf_1000028202.jpg"
                      alt="Capability Statement Preview"
                      className="rounded-sm shadow-md w-full"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="mb-8">
                <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">INQUIRY</p>
                <h2 className="font-heading text-2xl text-[#1a365d]">SEND US A MESSAGE</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#1a365d] font-medium">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                      placeholder="Your full name"
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#1a365d] font-medium">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                      placeholder="your@email.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#1a365d] font-medium">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                      placeholder="(555) 123-4567"
                      data-testid="input-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-[#1a365d] font-medium">
                      Company / Organization
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                      placeholder="Your company name"
                      data-testid="input-company"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inquiry_type" className="text-[#1a365d] font-medium">
                    Inquiry Type *
                  </Label>
                  <Select
                    value={formData.inquiry_type}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, inquiry_type: value }))
                    }
                  >
                    <SelectTrigger className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]" data-testid="select-inquiry-type">
                      <SelectValue placeholder="Select inquiry type" />
                    </SelectTrigger>
                    <SelectContent>
                      {inquiryTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value} data-testid={`option-${type.value}`}>
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#1a365d] font-medium">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d] resize-none"
                    placeholder="Please describe your inquiry, requirements, or how we can assist you..."
                    data-testid="input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-tactical btn-primary w-full md:w-auto text-sm"
                  data-testid="submit-button"
                >
                  {loading ? (
                    "Submitting..."
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Inquiry
                    </>
                  )}
                </Button>
              </form>

              {/* Quick Actions */}
              <div className="mt-12 pt-8 border-t border-slate-200">
                <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">QUICK ACTIONS</p>
                <div className="grid md:grid-cols-3 gap-4">
                  <button
                    onClick={() => setFormData((prev) => ({ ...prev, inquiry_type: "capability_statement" }))}
                    className="p-4 border-2 border-slate-200 rounded-sm hover:border-[#c5a04a] transition-colors text-left"
                    data-testid="quick-action-capability"
                  >
                    <FileText className="w-6 h-6 text-[#c5a04a] mb-2" />
                    <p className="font-heading text-sm text-[#1a365d]">Request Capability Statement</p>
                  </button>
                  <button
                    onClick={() => setFormData((prev) => ({ ...prev, inquiry_type: "partnership" }))}
                    className="p-4 border-2 border-slate-200 rounded-sm hover:border-[#c5a04a] transition-colors text-left"
                    data-testid="quick-action-partnership"
                  >
                    <Users className="w-6 h-6 text-[#c5a04a] mb-2" />
                    <p className="font-heading text-sm text-[#1a365d]">Partner With Us</p>
                  </button>
                  <button
                    onClick={() => setFormData((prev) => ({ ...prev, inquiry_type: "contracting" }))}
                    className="p-4 border-2 border-slate-200 rounded-sm hover:border-[#c5a04a] transition-colors text-left"
                    data-testid="quick-action-contracting"
                  >
                    <Briefcase className="w-6 h-6 text-[#c5a04a] mb-2" />
                    <p className="font-heading text-sm text-[#1a365d]">Contracting Inquiry</p>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
